import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, authFetch } from "@/lib/api";
import QRBox from "@/components/QRBox";
import CodiceVisita from "@/components/CodiceVisita";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog as ConfirmDialog, DialogContent as ConfirmContent, DialogHeader as ConfirmHeader, DialogTitle as ConfirmTitle, DialogFooter as ConfirmFooter } from "@/components/ui/dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { puoGestire, puoOperare } from "@/lib/ruoli";
import { toast } from "sonner";
import SceltaCommittente from "@/components/SceltaCommittente";
import {
  MapPin, Building2, Calendar, HardHat, Users, Truck, FileText,
  Upload, Archive, ExternalLink, Edit, UserPlus, Search, Trash2, AlertTriangle, IdCard, QrCode, ClipboardList, CheckCircle2,
  StickyNote, Plus, Download, ShieldQuestion, ChevronDown, Eye, EyeOff,
} from "lucide-react";
import { fileUrl } from "@/lib/placeholders";
import SiteAddressPicker from "@/components/SiteAddressPicker";
import SiteRouteMap from "@/components/SiteRouteMap";
import EquipmentTreePicker from "@/components/EquipmentTreePicker";
import { PallineFigure, CoperturaSicurezza } from "@/components/FigureSicurezza";
import AvvisoIndisponibili from "@/components/AvvisoIndisponibili";
import Briciole from "@/components/Briciole";
import { FascicoloDialog } from "@/pages/Dipendenti";
import BottoneRaccogli, { BottoneRaccogliTutti } from "@/components/BottoneRaccogli";
import NewReportDialog from "@/components/NewReportDialog";
import NomeDaIndirizzo from "@/components/NomeDaIndirizzo";
import { soloAssunti, oggiIso, bloccatoDaPratica,
         cosaMancaPratica } from "@/lib/disponibili";
import { mansioniDi } from "@/pages/Dipendenti";
import { dopoLaPersona, etichettaMezzo } from "@/lib/mezziAffidati";
import NewSiteCheckDialog from "@/components/NewSiteCheckDialog";
import CompilaPos from "@/components/CompilaPos";

const SITE_DOC_TYPES = [
  "POS", "Notifica preliminare", "Ordinanza", "Doc dal Committente",
  "Fir e Analisi", "Segnaletica", "Foto dei luoghi", "Altro",
];
const STATUS_LABEL = { attivo: "Attivo", sospeso: "Sospeso", chiuso: "Terminato" };

export default function CantiereDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  // Chi lavora sui cantieri: gli stessi che passano `require_tecnico` sul
  // server, amministrazione compresa.
  const isTecnico = puoOperare(user);
  // Eliminare un cantiere e' `require_gestione`: direzione e amministrazione.
  const isAdmin = puoGestire(user);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showQR, setShowQR] = useState(false);
  // Le spunte stanno qui e non dentro le singole schede: si spunta un
  // documento, poi un rilievo, poi gli appunti, e il pulsante li porta
  // via tutti insieme.
  const [scelte, setScelte] = useState({ documenti: [], lavagne: [], note: false });
  const spuntaDoc = (id, si) => setScelte((s) => ({
    ...s, documenti: si ? [...s.documenti, id] : s.documenti.filter(x => x !== id) }));
  const spuntaLav = (id, si) => setScelte((s) => ({
    ...s, lavagne: si ? [...s.lavagne, id] : s.lavagne.filter(x => x !== id) }));
  const svuotaScelte = () => setScelte({ documenti: [], lavagne: [], note: false });
  const [showReport, setShowReport] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [site, setSite] = useState(null);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState(null);
  // Dal QR del cartello: la verifica parte subito sul cantiere giusto.
  useEffect(() => {
    try {
      const azione = new URLSearchParams(window.location.search).get("azione");
      if (site && azione === "verifica") setShowCheck(true);
      if (site && azione === "segnala") setShowReport(true);
    } catch { /* niente */ }
  }, [site]);
  const [showAssign, setShowAssign] = useState(false);
  // La scheda aperta la tiene la pagina, cosi' i numeri in testata
  // possono portarci sopra con un clic.
  const [tab, setTab] = useState("team");
  const [showUpload, setShowUpload] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [types, setTypes] = useState([]);
  const [fascicoloEmp, setFascicoloEmp] = useState(null);
  const [compliance, setCompliance] = useState([]);
  // I sopralluoghi esterni: chi e' passato con il codice del preposto.
  const [visite, setVisite] = useState([]);
  const [visitaAperta, setVisitaAperta] = useState(null);
  useEffect(() => {
    api.get(`/visite/cantiere/${id}`).then(({ data }) => setVisite(data)).catch(() => setVisite([]));
  }, [id]);

  const doDelete = async () => {
    try {
      await api.delete(`/sites/${id}`);
      toast.success("Cantiere eliminato definitivamente");
      nav("/cantieri");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore durante l'eliminazione"); }
  };

  const loadCompliance = async () => {
    try {
      const { data } = await api.get(`/sites/${id}/compliance`);
      setCompliance(data);
    } catch { /* silenzioso: non bloccare la pagina per questo */ }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/sites/${id}`);
      setSite(data);
      setError(null);
      loadCompliance();
    } catch (e) {
      setError(e.response?.status === 403
        ? "Non hai accesso a questo cantiere."
        : "Cantiere non trovato.");
    }
  };
  const loadDocs = async () => {
    try {
      const { data } = await api.get(`/sites/${id}/documents`);
      setDocs(data);
    } catch { /* silenzioso, gestito da load() */ }
  };
  useEffect(() => {
    load(); loadDocs();
    api.get("/sites/activity-types").then(({ data }) => setTypes(data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [id]);

  if (error) return (
    <div className="space-y-4">
      <Briciole voci={[{ testo: "Cantieri", a: "/cantieri" },
                       { testo: "Cantiere non trovato" }]} />
      <Card className="border-red-200 bg-red-50"><CardContent className="p-8 text-center text-red-700">{error}</CardContent></Card>
    </div>
  );
  if (!site) return <div className="text-slate-500">Caricamento...</div>;

  const active = docs.filter(d => !d.archived);
  const archived = docs.filter(d => d.archived);

  return (
    <div className="space-y-6" data-testid="site-detail-page">
      <Briciole voci={[
        { testo: "Cantieri", a: "/cantieri" },
        { testo: site.name },
      ]} />

      {/* La testata sta su due piani: sopra il nome con le azioni, sotto i dati
          in tre colonne. Prima era tutto in una colonna sola a sinistra, e i
          pulsanti - che nel frattempo sono diventati sette - le rubavano lo
          spazio: la mappa si stringeva a un francobollo e mezzo schermo
          restava bianco. */}
      <Card className="border-slate-200">
        <CardContent className="p-6 space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-orange-600">
                {site.activity_type}
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">
                {site.name}
              </h1>
              <Badge className="mt-2">{STATUS_LABEL[site.status] || site.status}</Badge>
            </div>
            {isTecnico && (
              <div className="flex gap-2 flex-wrap lg:justify-end lg:max-w-[46rem]">
                <Button variant="outline" size="sm" onClick={() => setShowReport(true)} data-testid="site-report-btn">
                  <ClipboardList className="w-4 h-4 mr-2" />Segnala
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowCheck(true)} data-testid="site-check-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" />Verifica
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowQR(true)} data-testid="site-qr-btn">
                  <QrCode className="w-4 h-4 mr-2" /> QR pubblico
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowEdit(true)} data-testid="edit-site-btn">
                  <Edit className="w-4 h-4 mr-2" /> Modifica cantiere
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowAssign(true)} data-testid="assign-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Gestisci squadra e mezzi
                </Button>
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)}
                          className="text-red-600 hover:bg-red-50 border-red-200" data-testid="delete-site-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Elimina cantiere
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="grid gap-5 lg:grid-cols-12">
            <div className="lg:col-span-4 min-w-0 space-y-2">
              <div className="text-sm text-slate-600 flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
                <span>{site.address}</span>
              </div>
              {site.client && (
                <div className="text-sm text-slate-600 flex items-start gap-2">
                  <Building2 className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
                  <span>{site.client}</span>
                </div>
              )}
              {(site.start_date || site.end_date_planned) && (
                <div className="text-sm text-slate-600 flex items-start gap-2">
                  <Calendar className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
                  <span>
                    {site.start_date?.substring(0, 10) || "—"} →{" "}
                    {site.end_date_planned?.substring(0, 10) || "in corso"}
                    {/* Le giornate contate una per una in griglia: con i
                        buchi in mezzo, da 7 a 13 possono essere sei. */}
                    {site.giornate_pianificate > 0 && (
                      <span className="text-slate-500" data-testid="giornate-pianificate">
                        {" "}· {site.giornate_pianificate} {site.giornate_pianificate === 1 ? "giornata" : "giornate"} in griglia
                      </span>
                    )}
                  </span>
                </div>
              )}
              {site.notes && (
                <p className="text-sm text-slate-500 pt-1 whitespace-pre-wrap">{site.notes}</p>
              )}

              {/* Il codice di visita sta qui e non fra le azioni in alto: non e'
                  una cosa che si fa dalla scrivania, e in quella riga rubava lo
                  spazio a tutto il resto. Il preposto ce l'ha sulla sua
                  schermata; questo serve a tecnici e direzione quando sono loro
                  in cantiere. */}
              {isTecnico && (
                <div className="pt-3 mt-1 border-t border-slate-100">
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">
                    Chi viene a visitare il cantiere
                  </div>
                  <CodiceVisita cantiereId={site.id} cantiereNome={site.name}
                                variante="contorno" />
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    Il codice per il coordinatore o l&apos;ispettore che arriva sul
                    posto. Vale una volta sola.
                  </p>
                </div>
              )}
            </div>

            {/* I quattro numeri che si guardano per primi. Si clicca e si va
                alla scheda corrispondente. */}
            <div className="lg:col-span-3 grid grid-cols-2 gap-2 content-start">
              {[["team", "Squadra", site.employees?.length || 0, "text-slate-800"],
                ["mezzi", "Mezzi", site.equipment?.length || 0, "text-slate-800"],
                ["documenti", "Documenti", active.length, "text-slate-800"],
                ["team", "Non in regola", compliance.length,
                 compliance.length ? "text-amber-600" : "text-slate-300"]]
                .map(([vai, nome, quanti, colore], i2) => (
                <button key={i2} type="button" onClick={() => setTab(vai)}
                        data-testid={`riquadro-${nome.toLowerCase().replace(" ", "-")}`}
                        className="text-left border border-slate-200 rounded-lg px-3 py-2 hover:border-slate-300 transition-colors">
                  <div className={`font-display text-2xl font-black tabular-nums ${colore}`}>
                    {quanti}
                  </div>
                  <div className="text-[11px] text-slate-500 leading-tight">{nome}</div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-5 min-w-0">
              {site.duration_text
                ? <SiteRouteMap lat={site.lat} lng={site.lng} />
                : (
                  <div className="h-full min-h-[120px] border border-dashed border-slate-200 rounded-lg flex items-center justify-center text-xs text-slate-400 px-3 text-center">
                    Percorso non disponibile: manca la posizione del cantiere
                  </div>
                )}
            </div>
          </div>
        </CardContent>
      </Card>

      {visite.length > 0 && (
        <SopralluoghiEsterni visite={visite} aperta={visitaAperta} onApri={setVisitaAperta}
                             puoScaricare={user?.role === "admin" || user?.role === "amministrativo"} />
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="team" data-testid="tab-team">Team ({site.employees?.length || 0})</TabsTrigger>
          <TabsTrigger value="mezzi" data-testid="tab-mezzi">Mezzi ({site.equipment?.length || 0})</TabsTrigger>
          <TabsTrigger value="documenti" data-testid="tab-documenti">Documenti ({active.length})</TabsTrigger>
          <TabsTrigger value="note" data-testid="tab-note">Note</TabsTrigger>
          <TabsTrigger value="lavagna" data-testid="tab-lavagna">Lavagna</TabsTrigger>
          {isTecnico && (
            <TabsTrigger value="pos" data-testid="tab-pos">Compila POS</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="team" className="space-y-3">
          {compliance.length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-amber-900">
                      {compliance.length} dipendent{compliance.length === 1 ? "e" : "i"} con attestati non in regola per questo cantiere
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {compliance.map(c => (
                        <div key={c.employee_id} className="text-sm text-amber-800 flex flex-wrap items-center gap-1.5">
                          <button type="button" onClick={() => setFascicoloEmp({ id: c.employee_id, full_name: c.full_name, badge_number: c.badge_number })}
                                  className="font-semibold underline decoration-dotted">
                            {c.full_name}
                          </button>
                          {c.missing.map(m => <Badge key={m} variant="destructive" className="text-[10px]">manca: {m}</Badge>)}
                          {c.expired.map(e2 => <Badge key={e2} className="bg-amber-500 text-white border-0 text-[10px]">scaduto: {e2}</Badge>)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <h3 className="font-display font-bold mb-3 flex items-center gap-2">
                <HardHat className="w-4 h-4 text-orange-500" /> Cantiere visibile sui seguenti profili
              </h3>
              <p className="text-xs text-slate-500 -mt-2 mb-3">
                Le persone qui sotto, entrando nell'app dal loro tablet con l'indirizzo indicato,
                trovano questo cantiere già caricato nel loro profilo, con documenti e verifiche.
              </p>
              {(!site.preposti || site.preposti.length === 0)
                ? <p className="text-sm text-slate-500">
                    Nessuno per ora: il cantiere compare sul tablet di chi è in squadra e ha un
                    account preposto. Si aggiunge la persona alla squadra dall'elenco del personale,
                    come gli altri dipendenti.
                  </p>
                : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {site.preposti.map(p => (
                      <button key={p.id} type="button" disabled={!p.employee}
                              onClick={() => p.employee && setFascicoloEmp(p.employee)}
                              className={`text-left w-full p-3 border border-slate-200 rounded flex items-center gap-2 ${p.employee ? "hover:border-orange-300 transition cursor-pointer" : "cursor-default"}`}>
                        <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold text-sm flex-shrink-0">
                          {p.name?.[0] || "?"}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm truncate">{p.name}</div>
                          <div className="text-xs text-slate-500 truncate">{p.email}</div>
                        </div>
                        {p.employee && <ExternalLink className="w-3 h-3 text-orange-400 flex-shrink-0" />}
                      </button>
                    ))}
                  </div>}
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <h3 className="font-display font-bold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-orange-500" /> Personale impiegato
              </h3>
              {(!site.employees || site.employees.length === 0)
                ? <p className="text-sm text-slate-500">Nessun dipendente assegnato</p>
                : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {site.employees.map(e => (
                      <button key={e.id} type="button" onClick={() => setFascicoloEmp(e)}
                              className="text-left w-full px-3 py-1.5 border border-slate-200 rounded hover:border-orange-300 transition flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm flex items-center gap-1.5">
                            <span className="truncate">{e.full_name}</span>
                            {/* Il tesserino da far vedere quando arriva un
                                controllo e la persona non ce l'ha addosso. */}
                            <Link to={`/fascicolo/${e.id}?tesserino=1`} onClick={(ev) => ev.stopPropagation()}
                                  title="Mostra il tesserino" data-testid={`tesserino-${e.id}`}
                                  className="text-[10px] font-medium text-[#0067b2] border border-[#0067b2]/30 rounded px-1.5 py-0.5 flex-shrink-0 hover:bg-blue-50">
                              tesserino
                            </Link>
                            {e.is_preposto && (
                              <Badge className="bg-blue-100 text-[#0067b2] border-0 text-[9px] flex-shrink-0">preposto</Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{e.badge_number} · {e.role}</div>
                        </div>
                        <ExternalLink className="w-3 h-3 text-orange-400 flex-shrink-0" />
                      </button>
                    ))}
                  </div>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mezzi">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              {(!site.equipment || site.equipment.length === 0)
                ? <p className="text-sm text-slate-500">Nessuna attrezzatura assegnata</p>
                : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {site.equipment.map(e => (
                      <Link key={e.id} to={`/attrezzature/${e.id}`}>
                        <div className="px-3 py-2 border border-slate-200 rounded hover:border-orange-300 transition">
                          <div className="text-[10px] uppercase text-slate-500">{e.category}</div>
                          <div className="font-semibold text-sm">{[e.brand, e.model].filter(Boolean).join(" ") || e.identifier}</div>
                          <div className="text-xs font-mono text-slate-500">{e.identifier}</div>
                        </div>
                      </Link>
                    ))}
                  </div>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documenti" className="space-y-4">
          {isTecnico && (
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => setShowUpload(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
                <Upload className="w-4 h-4 mr-2" /> Carica documento
              </Button>
              <BottoneFrontespizio site={site} onFatto={loadDocs} />
              <BottoneRaccogliTutti
                etichetta="Tutti nel raccoglitore"
                voci={active.filter(d => d.file_url).map(d => ({
                  tipo: "cantiere", id: d.id, gruppo: site.name || site.address,
                  titolo: d.title || d.doc_type, sottotitolo: d.doc_type,
                }))} />
              <span className="ml-auto">
                <ScaricaSelezionati site={site} scelte={scelte} onSvuota={svuotaScelte} />
              </span>
            </div>
          )}
          {active.length === 0
            ? <p className="text-sm text-slate-500">Nessun documento caricato</p>
            : <div className="space-y-2">
                {active.map(d => (
                  <DocRow key={d.id} d={d} isTecnico={isTecnico} onChange={loadDocs}
                          gruppo={site.name || site.address}
                          spuntato={scelte.documenti.includes(d.id)}
                          onSpunta={spuntaDoc} />
                ))}
              </div>}
          {archived.length > 0 && (
            <>
              <h4 className="text-xs uppercase tracking-widest text-slate-500 mt-6">Archiviati</h4>
              <div className="space-y-2 opacity-70">
                {archived.map(d => <DocRow key={d.id} d={d} isTecnico={isTecnico} onChange={loadDocs}
                                          gruppo={site.name || site.address} archivedView />)}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="note" className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={scelte.note} data-testid="spunta-note"
                     onChange={(e) => setScelte((s) => ({ ...s, note: e.target.checked }))}
                     className="w-4 h-4 accent-orange-500" />
              Metti gli appunti nella cartella da scaricare
            </label>
            <span className="ml-auto">
              <ScaricaSelezionati site={site} scelte={scelte} onSvuota={svuotaScelte} />
            </span>
          </div>
          <NoteCantiere site={site} puoScrivere={isTecnico} />
        </TabsContent>

        <TabsContent value="lavagna">
          <LavagneCantiere site={site} puoDisegnare={isTecnico}
                           scelte={scelte} onSpunta={spuntaLav}
                           onSvuota={svuotaScelte} />
        </TabsContent>

        {isTecnico && (
          <TabsContent value="pos">
            <CompilaPos site={site} />
          </TabsContent>
        )}
      </Tabs>

      {showAssign && <AssignDialog site={site} onClose={() => setShowAssign(false)}
                                   onSaved={() => { setShowAssign(false); load(); }} />}
      {showEdit && <EditSiteDialog site={site} types={types} onClose={() => setShowEdit(false)}
                                  onSaved={() => { setShowEdit(false); load(); }} />}
      {showUpload && <UploadSiteDoc siteId={id} onClose={() => setShowUpload(false)}
                                    onSaved={() => { setShowUpload(false); loadDocs(); }} />}
      {showQR && (
        <Dialog open onOpenChange={() => setShowQR(false)}>
          <DialogContent className="text-center">
            <DialogHeader><DialogTitle>QR pubblico · {site.name}</DialogTitle></DialogHeader>
            <QRBox percorso={`/sites/${id}/qrcode`} titolo={site.name}
                   sottotitolo={site.address}
                   indirizzo={`${window.location.origin}/cantiere-pubblico/${id}`}
                   nomeFile={`cantiere_${site.name}`} />
            <p className="text-xs text-slate-400 mt-2">
              Chi scansiona questo codice vede solo indirizzo, committente, tipologia opere e i documenti
              marcati come "pubblici". Puoi scegliere quali documenti rendere visibili al momento del caricamento.
            </p>
          </DialogContent>
        </Dialog>
      )}
      {fascicoloEmp && <FascicoloDialog emp={fascicoloEmp} onClose={() => setFascicoloEmp(null)} />}
      {showReport && <NewReportDialog defaultSiteId={id} defaultSiteName={site.name} onClose={() => setShowReport(false)} onSaved={() => setShowReport(false)} />}
      {showCheck && <NewSiteCheckDialog defaultSiteId={id} defaultSiteName={site.name} onClose={() => setShowCheck(false)} onSaved={() => setShowCheck(false)} />}

      {confirmDelete && (
        <ConfirmDialog open onOpenChange={() => setConfirmDelete(false)}>
          <ConfirmContent>
            <ConfirmHeader>
              <ConfirmTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" /> Eliminare definitivamente questo cantiere?
              </ConfirmTitle>
            </ConfirmHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-700">Stai per eliminare <strong>{site.name}</strong>.</p>
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                Questa operazione è <strong>irreversibile</strong>: tutti i documenti di questo cantiere
                verranno cancellati definitivamente insieme alla scheda, e le assegnazioni di personale,
                mezzi e preposti andranno perse. Se vuoi solo conservarne la memoria senza più operarci,
                usa invece "Modifica cantiere" e imposta lo Stato su "Chiuso".
              </p>
            </div>
            <ConfirmFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(false)}>Annulla</Button>
              <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white">
                <Trash2 className="w-4 h-4 mr-2" /> Elimina definitivamente
              </Button>
            </ConfirmFooter>
          </ConfirmContent>
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * Il pulsante che porta via il cantiere.
 *
 * Si spuntano documenti, rilievi e appunti in giro per le schede, e da qui
 * escono in una cartella sola col nome dell'indirizzo. Prima si scaricavano
 * uno per uno, ognuno col nome che aveva, e sul computer diventavano una
 * manciata di PDF senza padre.
 *
 * Spento finché non si è spuntato niente: un pulsante che si illumina dice da
 * solo che adesso c'è qualcosa da prendere.
 */
function ScaricaSelezionati({ site, scelte, onSvuota }) {
  const [inCorso, setInCorso] = useState(false);
  const quanti = scelte.documenti.length + scelte.lavagne.length + (scelte.note ? 1 : 0);

  const scarica = async () => {
    setInCorso(true);
    try {
      const r = await authFetch(`/sites/${site.id}/archivio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scelte),
      });
      if (!r.ok) throw new Error();
      const cd = r.headers.get("content-disposition") || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || "cantiere.zip";
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
      toast.success("Cartella scaricata");
      onSvuota();
    } catch {
      toast.error("Errore nel download");
    } finally { setInCorso(false); }
  };

  return (
    <div className="flex items-center gap-2">
      {quanti > 0 && (
        <button type="button" onClick={onSvuota}
                className="text-xs text-slate-500 hover:underline">
          togli le spunte
        </button>
      )}
      <Button onClick={scarica} disabled={!quanti || inCorso}
              data-testid="scarica-selezionati"
              title="Scarica in una cartella sola quello che hai spuntato"
              className={quanti
                ? "bg-orange-500 hover:bg-orange-600 text-white"
                : "bg-slate-100 text-slate-400 hover:bg-slate-100"}>
        <Download className="w-4 h-4 mr-2" />
        {quanti ? `Scarica ${quanti}` : "Scarica"}
      </Button>
    </div>
  );
}

/**
 * I rilievi di sopralluogo di questo cantiere.
 *
 * Lo schizzo con le misure prese col metro: si apre sulla sua pagina, che è
 * tutta tela da disegno.
 */
function LavagneCantiere({ site, puoDisegnare, scelte, onSpunta, onSvuota }) {
  const [lavagne, setLavagne] = useState([]);
  const [titolo, setTitolo] = useState("");
  const [inCorso, setInCorso] = useState(false);
  const navigate = useNavigate();

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get(`/sites/${site.id}/lavagne`);
      setLavagne(data.lavagne || []);
    } catch { /* niente da dire */ }
  }, [site.id]);

  useEffect(() => { carica(); }, [carica]);

  const nuovo = async () => {
    setInCorso(true);
    try {
      const { data } = await api.post(`/sites/${site.id}/lavagne`, {
        titolo: titolo.trim() || `Sopralluogo del ${new Date().toLocaleDateString("it-IT")}`,
      });
      navigate(`/lavagna/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setInCorso(false); }
  };

  const togli = async (l) => {
    if (!window.confirm(`Eliminare il rilievo «${l.titolo}»?`)) return;
    try {
      await api.delete(`/lavagne/${l.id}`);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const quando = (iso) => (iso
    ? String(iso).substring(0, 10).split("-").reverse().join("/") : "");

  return (
    <div className="space-y-3" data-testid="lavagne-cantiere">
      <p className="text-xs text-slate-500">
        Lo schizzo del sopralluogo: si traccia sul foglio bianco o su una foto
        scattata sul posto, e le misure si scrivono a mano, come sul taccuino.
        Non è un disegno in scala.
      </p>

      {puoDisegnare && (
        <Card className="border-slate-200"><CardContent className="p-4 flex gap-2 flex-wrap">
          <Input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                 data-testid="titolo-lavagna" className="flex-1 min-w-[12rem]"
                 placeholder="Titolo del rilievo (facoltativo)" />
          <Button onClick={nuovo} disabled={inCorso} data-testid="nuova-lavagna"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-2" /> Nuovo rilievo
          </Button>
          {scelte && (
            <span className="ml-auto self-center">
              <ScaricaSelezionati site={site} scelte={scelte} onSvuota={onSvuota} />
            </span>
          )}
        </CardContent></Card>
      )}

      {lavagne.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="p-8 text-center">
          <p className="text-sm text-slate-500">Nessun rilievo su questo cantiere.</p>
        </CardContent></Card>
      ) : lavagne.map((l) => (
        <Card key={l.id} className={
          scelte?.lavagne.includes(l.id) ? "border-orange-300 bg-orange-50/60" : "border-slate-200"}>
          <CardContent className="p-4 flex items-center gap-3">
            {onSpunta && (
              <input type="checkbox" data-testid={`spunta-${l.id}`}
                     checked={scelte?.lavagne.includes(l.id) || false}
                     onChange={(e) => onSpunta(l.id, e.target.checked)}
                     className="w-4 h-4 flex-shrink-0 accent-orange-500" />
            )}
            <div className="flex-1 min-w-0">
              <Link to={`/lavagna/${l.id}`}
                    className="font-semibold text-[#0067b2] hover:underline">
                {l.titolo}
              </Link>
              <div className="text-[11px] text-slate-400">
                {l.creata_da} · {quando(l.creata_il)}
                {l.modificata_il && ` · ritoccato il ${quando(l.modificata_il)}`}
                {l.sfondo && " · su foto"}
              </div>
            </div>
            <Link to={`/lavagna/${l.id}`}>
              <Button variant="outline" size="sm">Apri</Button>
            </Link>
            {puoDisegnare && (
              <Button variant="outline" size="sm" onClick={() => togli(l)}
                      className="text-red-600 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Gli appunti di cantiere.
 *
 * Testo libero, ma non un unico foglio da riscrivere: appunti separati, ognuno
 * con chi l'ha scritto e quando. Un numero di telefono trovato in un
 * sopralluogo di marzo vale se si sa che è di marzo e chi l'ha preso; dentro
 * un blocco riscritto dieci volte quella storia si perde.
 */
function NoteCantiere({ site, puoScrivere }) {
  const [note, setNote] = useState([]);
  const [testo, setTesto] = useState("");
  const [inCorso, setInCorso] = useState(false);
  const [modifica, setModifica] = useState(null);

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get(`/sites/${site.id}/note`);
      setNote(data.note || []);
    } catch { /* il cantiere può non essere accessibile: niente da dire */ }
  }, [site.id]);

  useEffect(() => { carica(); }, [carica]);

  const salva = async () => {
    if (!testo.trim()) return;
    setInCorso(true);
    try {
      await api.post(`/sites/${site.id}/note`, { testo });
      setTesto("");
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setInCorso(false); }
  };

  const correggi = async (n, nuovo) => {
    try {
      await api.put(`/sites/${site.id}/note/${n.id}`, { testo: nuovo });
      setModifica(null);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const togli = async (n) => {
    if (!window.confirm("Eliminare questo appunto?")) return;
    try {
      await api.delete(`/sites/${site.id}/note/${n.id}`);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const quando = (iso) => {
    if (!iso) return "";
    const g = String(iso).substring(0, 10).split("-").reverse().join("/");
    const o = String(iso).substring(11, 16);
    return o ? `${g} alle ${o}` : g;
  };

  return (
    <div className="space-y-3" data-testid="note-cantiere">
      {puoScrivere && (
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-2">
            <Label>Nuovo appunto</Label>
            <textarea value={testo} rows={3} data-testid="nuova-nota"
                      onChange={(e) => setTesto(e.target.value)}
                      placeholder="Sopralluogo, telefoni, accessi, accordi presi: quello che serve ricordare di questo cantiere."
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <div className="flex justify-end">
              <Button onClick={salva} disabled={inCorso || !testo.trim()}
                      data-testid="salva-nota"
                      className="bg-orange-500 hover:bg-orange-600 text-white">
                <Plus className="w-4 h-4 mr-2" /> Aggiungi
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {note.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="p-8 text-center">
          <StickyNote className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Nessun appunto su questo cantiere.</p>
        </CardContent></Card>
      ) : note.map((n) => (
        <Card key={n.id} className="border-slate-200">
          <CardContent className="p-4">
            {modifica === n.id ? (
              <ModificaNota nota={n} onSalva={(v) => correggi(n, v)}
                            onAnnulla={() => setModifica(null)} />
            ) : (
              <>
                <p className="text-sm whitespace-pre-wrap">{n.testo}</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] text-slate-400">
                    {n.scritta_da} · {quando(n.scritta_il)}
                    {n.corretta_il && ` · corretto da ${n.corretta_da} il ${quando(n.corretta_il)}`}
                  </span>
                  {puoScrivere && (
                    <span className="ml-auto flex items-center gap-1">
                      <Button variant="outline" size="sm"
                              onClick={() => setModifica(n.id)}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => togli(n)}
                              className="text-red-600 hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </span>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ModificaNota({ nota, onSalva, onAnnulla }) {
  const [testo, setTesto] = useState(nota.testo || "");
  return (
    <div className="space-y-2">
      <textarea value={testo} rows={4} data-testid="correggi-nota"
                onChange={(e) => setTesto(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onAnnulla}>Lascia com&apos;è</Button>
        <Button size="sm" onClick={() => onSalva(testo)} disabled={!testo.trim()}
                className="bg-orange-500 hover:bg-orange-600 text-white">
          Salva
        </Button>
      </div>
    </div>
  );
}

/**
 * Il pulsante che si accende quando il cantiere ha tutto quello che serve al
 * frontespizio del POS. Spento, dice cosa manca e dove trovarlo: un elenco di
 * cose da sistemare vale molto meglio di un pulsante grigio senza spiegazione.
 */
function BottoneFrontespizio({ site, onFatto }) {
  const [stato, setStato] = useState(null);
  const [apri, setApri] = useState(false);

  const [errore, setErrore] = useState(null);

  const guarda = useCallback(async () => {
    try {
      const { data } = await api.get(`/sites/${site.id}/pos/prontezza`);
      setStato(data); setErrore(null);
    } catch (e) {
      setStato(null);
      setErrore(e.response?.data?.detail || "Non riesco a leggere lo stato del POS");
    }
  }, [site.id]);

  useEffect(() => { guarda(); }, [guarda]);

  // Se la lettura non riesce il pulsante spariva e basta, e sembrava che il
  // POS non si potesse fare. Meglio un pulsante che dice cos'e' andato storto.
  if (errore) {
    return (
      <Button variant="outline" className="text-amber-700 border-amber-300" onClick={guarda}
              data-testid="frontespizio-errore" title={errore}>
        <AlertTriangle className="w-4 h-4 mr-2" /> POS non leggibile · riprova
      </Button>
    );
  }
  if (!stato) return null;
  const pronto = stato.pronto;
  // Il POS in corso non descrive più il cantiere: qualcosa è cambiato dopo
  // che è stato stampato, e il foglio che sta in cantiere adesso dice il
  // falso. È la cosa più urgente che questo pulsante possa dire.
  const superato = stato.pos?.da_rigenerare;

  return (<>
    <Button variant={pronto && !superato ? "default" : "outline"}
            onClick={() => setApri(true)} data-testid="genera-frontespizio"
            className={superato
              ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
              : pronto
                ? "bg-[#0067b2] hover:bg-[#005596] text-white"
                : "text-slate-500"}>
      {superato ? <AlertTriangle className="w-4 h-4 mr-2" />
                : <FileText className="w-4 h-4 mr-2" />}
      {stato.pos?.generato
        ? `POS di cantiere · rev. ${stato.pos.revisione}`
        : "Genera frontespizio POS"}
      {superato && (
        <span className="ml-2 text-[10px] bg-white/25 border border-white/40 rounded-full px-2 py-0.5">
          {stato.pos.differenze.length} da aggiornare
        </span>
      )}
      {!superato && !pronto && (
        <span className="ml-2 text-[10px] bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
          mancano {stato.mancano.length}
        </span>
      )}
      {!superato && pronto && (stato.avvisi || []).length > 0 && (
        <span className="ml-2 text-[10px] bg-white/20 border border-white/30 rounded-full px-2 py-0.5">
          {stato.avvisi.length} da controllare
        </span>
      )}
    </Button>

    {apri && (
      <DialogFrontespizio site={site} stato={stato}
                          onChiudi={() => { setApri(false); guarda(); }}
                          onFatto={() => { onFatto?.(); guarda(); }} />
    )}
  </>);
}

function DialogFrontespizio({ site, stato, onChiudi, onFatto }) {
  const [ruoli, setRuoli] = useState([]);
  const [ruoliScelti, setRuoliScelti] = useState([]);
  const [busy, setBusy] = useState(false);

  // Le schede da allegare non si scelgono qui: stanno nella scheda Compila
  // POS, dove restano salvate. Qui si dice soltanto quante ne risultano.
  useEffect(() => {
    api.get("/pos/ruoli")
      .then(({ data }) => {
        setRuoli(data);
        // Si propongono spuntati i ruoli per cui un nome risulta: quelli noti
        // al sistema e quelli scritti sulla scheda del cantiere.
        const compilati = site.ruoli || {};
        setRuoliScelti(data.filter(
          (x) => x.automatico || x.dalla_squadra
                 || (compilati[x.codice] || "").trim()).map((x) => x.codice));
      })
      .catch(() => toast.error("Errore nel caricamento dei ruoli"));
  }, [site.ruoli]);

  const alterna = (elenco, set, codice) =>
    set(elenco.includes(codice) ? elenco.filter((x) => x !== codice)
                                : [...elenco, codice]);

  const genera = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sites/${site.id}/pos/frontespizio`, {
        ruoli: ruoliScelti, salva_nei_documenti: true,
      });
      toast.success(`Frontespizio creato: ${data.pagine} pagine, ${data.schede} schede`);
      if (data.schede_saltate?.length) {
        toast.warning(`Non allegate: ${data.schede_saltate.join(", ")}`);
      }
      onFatto?.();
      onChiudi();
    } catch (e) {
      // Il messaggio del server va mostrato per esteso e lasciato sullo
      // schermo: "errore nella generazione" non dice niente a nessuno.
      const risposta = e.response?.data;
      const dettaglio = (typeof risposta === "string" ? risposta : risposta?.detail)
        || e.message || "errore sconosciuto";
      toast.error(`POS non generato · ${dettaglio}`, { duration: 20000 });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Frontespizio POS di cantiere</DialogTitle></DialogHeader>

        {stato.pos?.da_rigenerare && (
          <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm space-y-2"
               data-testid="pos-da-rigenerare">
            <p className="font-semibold text-amber-900">
              Il POS in corso (rev. {stato.pos.revisione}) non descrive più questo cantiere.
            </p>
            <p className="text-amber-900/80 text-xs">
              È stato generato il{" "}
              {String(stato.pos.generato_il || "").substring(0, 10).split("-").reverse().join("/")}.
              Da allora è cambiato questo:
            </p>
            <ul className="space-y-0.5">
              {stato.pos.differenze.map((d, i) => (
                <li key={i} className="text-xs flex gap-2">
                  <span className={`font-semibold flex-shrink-0 ${
                    d.come === "aggiunto" ? "text-emerald-700"
                      : d.come === "tolto" ? "text-red-700" : "text-amber-800"}`}>
                    {d.come}
                  </span>
                  <span className="text-slate-700">
                    <strong>{d.cosa}</strong> · {d.dettaglio}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-amber-900">
              Per riportare queste modifiche sul documento bisogna rigenerarlo:
              diventerà la <strong>revisione {stato.pos.revisione + 1}</strong> e
              la precedente andrà in archivio, dove resta consultabile.
            </p>
          </div>
        )}

        {stato.pos?.senza_istantanea && (
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600">
            Il POS in corso è stato generato prima che l&apos;app tenesse traccia
            dei dati usati: non si può dire se sia ancora aggiornato.
            Rigenerandolo, da lì in avanti l&apos;app se ne accorge da sola.
          </div>
        )}

        {!stato.pronto ? (
          <div className="space-y-3">
            <p className="text-sm">
              Non ci sono ancora tutti i dati. Sistema questi e il pulsante si accende:
            </p>
            <div className="border border-amber-200 bg-amber-50 rounded divide-y divide-amber-100">
              {stato.mancano.map((m, i) => (
                <div key={i} className="p-2.5">
                  <div className="text-sm font-medium">{m.cosa}</div>
                  <div className="text-xs text-amber-800">{m.dove}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2.5">
              Modello <span className="font-semibold">{stato.modello_nome}</span>
              {stato.committente && <> · committente <span className="font-semibold">{stato.committente}</span></>}
              {" "}· {stato.persone} in squadra · {stato.mezzi} mezzi
              {" "}· {stato.sezioni_compilate} sezioni su {stato.sezioni_totali} compilate
            </div>
            {(stato.avvisi || []).length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded p-2.5 space-y-1">
                <div className="text-xs font-semibold text-amber-900">
                  Manca qualcosa, ma puoi generare lo stesso
                </div>
                {stato.avvisi.map((a, i) => (
                  <div key={i} className="text-xs text-amber-800">
                    · {a.cosa} — <span className="text-amber-700">{a.dove}</span>
                  </div>
                ))}
              </div>
            )}
            {stato.sezioni_compilate === 0 && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                Nessuna sezione compilata: il documento uscirà con la sola anagrafica.
                Le interferenze, gli scavi, gli agenti e i DPI si spuntano nella scheda
                <span className="font-semibold"> Compila POS</span>.
              </p>
            )}

            <div>
              <div className="text-sm font-semibold mb-1.5">Ruoli da stampare</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                {ruoli.map((r) => {
                  const nome = r.automatico
                    || (r.dalla_squadra ? (stato.preposti || []).join(" · ") : null)
                    || (site.ruoli || {})[r.codice];
                  return (
                    <label key={r.codice}
                           className={`flex items-start gap-2 text-xs p-1.5 rounded cursor-pointer
                                       ${nome ? "hover:bg-slate-50" : "opacity-40"}`}>
                      <input type="checkbox" className="mt-0.5" disabled={!nome}
                             checked={ruoliScelti.includes(r.codice)}
                             onChange={() => alterna(ruoliScelti, setRuoliScelti, r.codice)} />
                      <span className="min-w-0">
                        <span className="block truncate">{r.nome}</span>
                        <span className="block text-[10px] text-slate-500 truncate">
                          {nome || "nessun nome indicato"}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2.5">
              {stato.schede_scelte > 0 ? (
                <>
                  <span className="font-semibold">{stato.schede_scelte} schede</span>
                  {" "}saranno allegate in coda al frontespizio. Si spuntano nella
                  scheda <span className="font-semibold">Compila POS</span>, sezione 7.
                </>
              ) : (
                <>
                  Nessuna scheda di sicurezza spuntata: uscirà il solo frontespizio.
                  Le schede si scelgono nella scheda{" "}
                  <span className="font-semibold">Compila POS</span>, sezione 7.
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          {stato.pronto && (
            <Button onClick={genera} disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="conferma-frontespizio">
              {busy ? "Preparo..." : "Genera e salva nei documenti"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocRow({ d, isTecnico, onChange, archivedView, spuntato, onSpunta, gruppo }) {
  const archive = async () => { await api.post(`/sites/documents/${d.id}/archive`); onChange(); };
  // Pubblico vuol dire che lo vede chi apre il cantiere con il codice visita.
  // Se ci finisce per sbaglio si toglie da qui, senza ricaricare il file.
  const cambiaVisibilita = async () => {
    try {
      await api.put(`/sites/documents/${d.id}/visibilita`, { public: !d.public });
      toast.success(d.public ? "Ora è riservato" : "Ora è visibile a chi entra con il codice");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel cambio di visibilità"); }
  };
  return (
    <div className={`p-3 border rounded flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap ${
      spuntato ? "border-orange-300 bg-orange-50/60" : "border-slate-200"}`}>
      {onSpunta && (
        <input type="checkbox" checked={!!spuntato} data-testid={`spunta-${d.id}`}
               onChange={(e) => onSpunta(d.id, e.target.checked)}
               className="w-4 h-4 flex-shrink-0 accent-orange-500" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm flex items-center gap-2">
          <FileText className="w-4 h-4 text-orange-500 flex-shrink-0" /><span className="truncate">{d.title}</span>
        </div>
        <div className="text-xs text-slate-500 truncate flex items-center gap-1 flex-wrap">
          <span>{d.doc_type} · {d.filename}{d.expires_date && ` · scad. ${d.expires_date.substring(0, 10)}`}</span>
          {d.public
            ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]">pubblico</Badge>
            : <Badge className="bg-slate-100 text-slate-500 border-0 text-[9px]">riservato</Badge>}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {d.file_url && (
          <BottoneRaccogli voce={{ tipo: "cantiere", id: d.id, gruppo: gruppo || "Cantiere",
                                   titolo: d.title || d.doc_type, sottotitolo: d.doc_type }}
                           className="h-9 w-9 p-0" />
        )}
        <a href={fileUrl(d.file_url)} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm"><ExternalLink className="w-3 h-3 mr-1" />Apri</Button>
        </a>
        {isTecnico && !archivedView && (
          <Button variant="outline" size="sm" onClick={cambiaVisibilita}
                  data-testid={`visibilita-${d.id}`}
                  title={d.public
                    ? "Adesso lo vede chi entra con il codice visita: tocca per renderlo riservato"
                    : "Adesso lo vedono solo i nostri: tocca per mostrarlo a chi entra con il codice visita"}>
            {d.public ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          </Button>
        )}
        {isTecnico && !archivedView && (
          <Button variant="outline" size="sm" onClick={archive} title="Archivia">
            <Archive className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

const SITE_PUBLIC_DEFAULT_TYPES = new Set([
  "Notifica preliminare", "Ordinanza",
]);

function UploadSiteDoc({ siteId, onClose, onSaved }) {
  const [f, setF] = useState({
    doc_type: SITE_DOC_TYPES[0], title: "", issued_date: "", expires_date: "",
    public: SITE_PUBLIC_DEFAULT_TYPES.has(SITE_DOC_TYPES[0]),
  });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) return toast.error("Seleziona almeno un file");
    setBusy(true);
    const fd = new FormData();
    fd.append("doc_type", f.doc_type);
    if (f.title) fd.append("title", f.title);
    if (f.issued_date) fd.append("issued_date", f.issued_date);
    if (f.expires_date) fd.append("expires_date", f.expires_date);
    fd.append("public", f.public ? "true" : "false");
    files.forEach(file => fd.append("files", file));
    try {
      if (files.length === 1) {
        const single = new FormData();
        single.append("doc_type", f.doc_type);
        if (f.title) single.append("title", f.title);
        if (f.issued_date) single.append("issued_date", f.issued_date);
        if (f.expires_date) single.append("expires_date", f.expires_date);
        single.append("public", f.public ? "true" : "false");
        single.append("file", files[0]);
        await api.post(`/sites/${siteId}/documents`, single, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Documento caricato");
      } else {
        const { data } = await api.post(`/sites/${siteId}/documents/bulk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${data.imported} documenti caricati`);
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Carica documento di cantiere</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Tipo documento</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3"
                    value={f.doc_type}
                    onChange={(e) => setF({ ...f, doc_type: e.target.value, public: SITE_PUBLIC_DEFAULT_TYPES.has(e.target.value) })}>
              {SITE_DOC_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            {f.doc_type === "Altro" && (
              <p className="text-[10px] text-amber-600 mt-1">Indica il dettaglio nel campo Titolo qui sotto.</p>
            )}
          </div>
          <div><Label>Titolo</Label>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                   placeholder={f.doc_type === "Altro" ? "Descrivi il documento..." : "opzionale"} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data emissione</Label>
              <Input type="date" value={f.issued_date} onChange={(e) => setF({ ...f, issued_date: e.target.value })} /></div>
            <div><Label>Scadenza</Label>
              <Input type="date" value={f.expires_date} onChange={(e) => setF({ ...f, expires_date: e.target.value })} /></div>
          </div>
          <div>
            <Label>File * <span className="text-slate-400 font-normal">(puoi selezionarne più di uno)</span></Label>
            <Input type="file" accept=".pdf,image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} required />
            {files.length > 1 && (
              <p className="text-[10px] text-slate-500 mt-1">
                {files.length} file selezionati — verranno caricati tutti con lo stesso tipo, titolo e date.
              </p>
            )}
          </div>
          <label className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-200 rounded cursor-pointer" data-testid="checkbox-public-doc">
            <input type="checkbox" className="mt-0.5" checked={f.public}
                   onChange={(e) => setF({ ...f, public: e.target.checked })} />
            <span className="text-xs text-slate-600">
              <strong>Visibile pubblicamente</strong> — sarà consultabile da chiunque scansioni il QR
              del cantiere, senza necessità di accesso all'app.
            </span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white">
              {busy ? "Caricamento..." : "Carica"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ site, onClose, onSaved }) {
  const [employees, setEmployees] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [selEmployees, setSelEmployees] = useState((site.employees || []).map(e => e.id));
  const [selEquipment, setSelEquipment] = useState((site.equipment || []).map(e => e.id));
  const [skipWeekends, setSkipWeekends] = useState(site.skip_weekends !== false);
  const [q2, setQ2] = useState("");
  const [mansione, setMansione] = useState("");

  const [assenze, setAssenze] = useState([]);

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
    api.get("/equipment").then(({ data }) => setEquipment(data)).catch(() => {});
    // Il cantiere copre piu' giorni: interessa chi manca in un qualsiasi
    // momento del periodo, non solo oggi.
    if (site.start_date && site.end_date_planned) {
      api.get(`/leave-requests/in-range?date_from=${site.start_date}&date_to=${site.end_date_planned}`)
        .then(({ data }) => setAssenze(data)).catch(() => {});
    }
  }, [site.start_date, site.end_date_planned]);

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

  // Spuntando una persona arriva anche il mezzo che le e' affidato - quello
  // con il suo nome come utilizzatore, lo stesso che si vede sulla
  // localizzazione - e togliendola se ne va con lei. `mezziAuto` ricorda
  // quali sono arrivati cosi': un mezzo aggiunto a mano resta dov'e'.
  const [mezziAuto, setMezziAuto] = useState([]);

  const tocca = (empId) => {
    const entra = !selEmployees.includes(empId);
    const personeDopo = entra ? [...selEmployees, empId] : selEmployees.filter((x) => x !== empId);
    setSelEmployees(personeDopo);
    const esito = dopoLaPersona({
      id: empId, entra, personeDopo, mezzi: selEquipment, auto: mezziAuto, equipment,
    });
    setSelEquipment(esito.mezzi);
    setMezziAuto(esito.auto);
    const nome = (employees.find((x) => x.id === empId) || {}).full_name || "";
    if (esito.aggiunti?.length) {
      toast.message("Arriva anche il mezzo", {
        description: esito.aggiunti
          .map((m) => etichettaMezzo(equipment.find((q) => q.id === m)))
          .join(", ") + ` · affidato a ${nome}`,
      });
    }
    if (esito.fermi?.length) {
      toast.warning(`${etichettaMezzo(esito.fermi[0])} è fermo`, {
        description: `È il mezzo di ${nome}: scegline un altro a mano.`,
      });
    }
  };



  const submit = async () => {
    try {
      const { data } = await api.put(`/sites/${site.id}/assign`, {
        employee_ids: selEmployees, equipment_ids: selEquipment,
        skip_weekends: skipWeekends,
      });
      const sync = data.planning_sync;
      if (sync && sync.populated_days > 0) {
        const dove = sync.squad_name ? ` su ${sync.squad_name}` : "";
        const nuova = sync.created_squads?.length ? " (squadra creata ora)" : "";
        toast.success(`Squadra e mezzi aggiornati · Pianificati ${sync.populated_days} giorni${dove}${nuova}`);
      } else {
        toast.success("Squadra e mezzi aggiornati");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const filt = (list, q, keys) => list.filter(x =>
    !q || keys.some(k => (x[k] || "").toLowerCase().includes(q.toLowerCase())));

  // Le mansioni presenti in anagrafica, con quante persone ciascuna. Chi e'
  // gia' selezionato resta sempre in elenco, altrimenti cambiando mansione
  // sparirebbe dalla vista pur restando assegnato.
  // Chi sa fare piu' di un mestiere si conta sotto ciascuno: la tendina
  // risponde a «chi mi sa fare questo», non a come e' inquadrato.
  const mansioni = Object.entries(
    employees.reduce((conta, e) => {
      const sue = mansioniDi(e);
      for (const m of (sue.length ? sue : ["Senza mansione"])) {
        conta[m] = (conta[m] || 0) + 1;
      }
      return conta;
    }, {})).sort((a, b) => a[0].localeCompare(b[0]));

  // Chi entra in azienda piu' avanti non compare fra gli assegnabili: il
  // cantiere comincia oggi, e chi non c'e' ancora non ci si puo' mandare.
  // Resta in elenco se era gia' stato assegnato, altrimenti sparirebbe senza
  // che nessuno possa toglierlo.
  const inForza = employees.filter(
    (e) => selEmployees.includes(e.id)
           || soloAssunti([e], site.start_date > oggiIso() ? site.start_date : oggiIso()).length);
  const visibili = filt(inForza, q2, ["full_name", "badge_number"]).filter(
    (e) => !mansione
           || (mansioniDi(e).length ? mansioniDi(e) : ["Senza mansione"]).includes(mansione)
           || selEmployees.includes(e.id));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Gestisci squadra e mezzi · {site.name}</DialogTitle></DialogHeader>

        {/* Chi e cosa e' selezionato, in cima e in chiaro: le due colonne
            sotto sono lunghe e scorrono, e per sapere se un mezzo era gia'
            spuntato bisognava aprire la categoria e cercarlo. La x toglie.
            E' lo stesso riquadro della pianificazione, di proposito: la
            stessa cosa si fa allo stesso modo nelle due schermate. */}
        {(selEmployees.length > 0 || selEquipment.length > 0) && (
          <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2"
               data-testid="riepilogo-selezione">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Già in cantiere · {selEmployees.length}{" "}
              {selEmployees.length === 1 ? "persona" : "persone"}
              {selEquipment.length > 0
                && ` · ${selEquipment.length} ${selEquipment.length === 1 ? "mezzo" : "mezzi"}`}
            </div>
            <div className="flex flex-wrap gap-1">
              {selEmployees.map(id => {
                const e = employees.find(x => x.id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 rounded-full pl-2 pr-1 py-0.5">
                    {e?.full_name || id}
                    <button type="button" title="Togli"
                            onClick={() => tocca(id)}
                            className="text-slate-400 hover:text-red-500 leading-none px-0.5">×</button>
                  </span>
                );
              })}
              {selEquipment.map(id => {
                const m = equipment.find(x => x.id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1 text-[11px] bg-blue-50 border border-blue-200 text-blue-800 rounded-full pl-2 pr-1 py-0.5">
                    {m ? `${m.identifier}${m.model ? ` · ${m.model}` : ""}` : id}
                    <button type="button" title="Togli"
                            onClick={() => toggle(selEquipment, setSelEquipment, id)}
                            className="text-blue-400 hover:text-red-500 leading-none px-0.5">×</button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <AvvisoIndisponibili contesto="nel periodo del cantiere"
          assenti={selEmployees.map(id => {
            const a = assenze.find(x => x.employee_id === id && !x.solo_parziale);
            if (!a) return null;
            const piena = a.assenze.find(x => !x.parziale) || a.assenze[0];
            return { id, full_name: a.employee_name, etichetta: piena.etichetta,
                     periodo: `${piena.date_from.substring(5)} → ${piena.date_to.substring(5)}` };
          }).filter(Boolean)}
          parziali={selEmployees.map(id => {
            const a = assenze.find(x => x.employee_id === id && x.solo_parziale);
            if (!a) return null;
            const p = a.assenze[0];
            return { id, full_name: a.employee_name, etichetta: p.etichetta,
                     ora_inizio: p.ora_inizio, ora_fine: p.ora_fine };
          }).filter(Boolean)} />

        <CoperturaSicurezza
          dipendentiSelezionati={employees.filter(e => selEmployees.includes(e.id))} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1"><Users className="w-3 h-3" />Dipendenti</Label>
            <div className="grid grid-cols-2 gap-2 mt-1 mb-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <Input className="pl-7 h-9 text-sm" value={q2}
                       onChange={(e) => setQ2(e.target.value)} placeholder="Cerca..." />
              </div>
              {/* Con quaranta persone in elenco, scorrerle tutte per trovare un
                  operatore e' una perdita di tempo: prima si sceglie la
                  mansione, poi la persona. */}
              <select value={mansione} onChange={(e) => setMansione(e.target.value)}
                      data-testid="filtro-mansione"
                      className="h-9 border border-slate-200 rounded px-2 text-sm bg-white">
                <option value="">Tutte le mansioni</option>
                {mansioni.map(([nome, quanti]) => (
                  <option key={nome} value={nome}>{nome} ({quanti})</option>
                ))}
              </select>
            </div>
            <div className="border border-slate-200 rounded max-h-64 overflow-y-auto divide-y divide-slate-100">
              {visibili.length === 0 && (
                <div className="p-3 text-xs text-slate-400">Nessuna persona con questo filtro</div>
              )}
              {visibili.map(e => {
                // Pratica di assunzione aperta: resta in elenco - sparire
                // sembrerebbe un errore dell'app - ma non si puo' spuntare, e
                // c'e' scritto cosa manca. Chi e' gia' assegnato si puo'
                // sempre togliere.
                const bloccato = bloccatoDaPratica(e) && !selEmployees.includes(e.id);
                return (
                <label key={e.id} title={bloccato
                         ? `Pratica di assunzione da concludere · manca ${cosaMancaPratica(e)}`
                         : undefined}
                       className={`flex items-center gap-2 p-2 text-sm ${
                         bloccato ? "bg-red-50/60 text-slate-400 cursor-not-allowed"
                                  : "hover:bg-slate-50 cursor-pointer"}`}>
                  <input type="checkbox" checked={selEmployees.includes(e.id)}
                         disabled={bloccato}
                         onChange={() => tocca(e.id)} />
                  <span className="truncate flex-1">{e.full_name}</span>
                  {bloccato && (
                    <span className="text-[9px] font-semibold text-red-600 flex-shrink-0">
                      pratica aperta
                    </span>
                  )}
                  {(() => {
                    const a = assenze.find(x => x.employee_id === e.id);
                    if (!a) return null;
                    return (
                      <span className={`text-[9px] font-semibold flex-shrink-0 ${
                        a.solo_parziale ? "text-amber-700" : "text-red-600"}`}>
                        {a.solo_parziale ? "parziale" : a.assenze[0].etichetta}
                      </span>
                    );
                  })()}
                  <PallineFigure figure={e.figure_sicurezza} />
                  {e.is_preposto && (
                    <Badge className="bg-blue-100 text-[#0067b2] border-0 text-[9px] flex-shrink-0 gap-0.5">
                      <HardHat className="w-2.5 h-2.5" />preposto
                    </Badge>
                  )}
                </label>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              Chi ha il contrassegno preposto vedrà questo cantiere sul proprio profilo.
            </p>
          </div>
          <div>
            <Label className="flex items-center gap-1"><Truck className="w-3 h-3" />Attrezzature</Label>
            <EquipmentTreePicker equipment={equipment} selected={selEquipment}
                                 onToggle={(eid) => toggle(selEquipment, setSelEquipment, eid)} />
          </div>
        </div>
        <label className="flex items-start gap-2 text-sm bg-slate-50 border border-slate-200 rounded p-3 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={skipWeekends} data-testid="skip-weekends"
                 onChange={(e) => setSkipWeekends(e.target.checked)} />
          <span>
            Escludi sabato e domenica
            <span className="block text-[11px] text-slate-500">
              Sui periodi lunghi la pianificazione salta il fine settimana. Togli la spunta se la
              squadra lavorerà anche il sabato o la domenica.
            </span>
          </span>
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} className="bg-orange-500 hover:bg-orange-600 text-white">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditSiteDialog({ site, types, onClose, onSaved }) {
  const [f, setF] = useState({
    name: site.name || "",
    address: site.address || "",
    client: site.client || "",
    client_id: site.client_id || null,
    activity_type: site.activity_type || (types[0] || ""),
    start_date: site.start_date || "",
    end_date_planned: site.end_date_planned || "",
    notes: site.notes || "",
    status: site.status || "attivo",
    comune: site.comune || "", provincia: site.provincia || "", cap: site.cap || "",
    lat: site.lat || null, lng: site.lng || null,
    distance_text: site.distance_text || null, duration_text: site.duration_text || null,
    distance_value: site.distance_value || null, duration_value: site.duration_value || null,
    skip_weekends: site.skip_weekends !== false,
    numero_commessa: site.numero_commessa || "",
    pos_numero: site.pos_numero || "",
    ruoli: site.ruoli || {},
  });
  const [ruoli, setRuoli] = useState([]);

  useEffect(() => {
    api.get("/pos/ruoli").then(({ data }) => setRuoli(data)).catch(() => {});
  }, []);

  const scriviRuolo = (codice, nome) =>
    setF((p) => ({ ...p, ruoli: { ...p.ruoli, [codice]: nome } }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(`/sites/${site.id}`, f);
      const sync = data?.planning_sync;
      toast.success(sync?.populated_days
        ? `Cantiere aggiornato · pianificazione riallineata su ${sync.populated_days} giorni${sync.squad_name ? ` (${sync.squad_name})` : ""}`
        : "Cantiere aggiornato");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }} onInteractOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }} onFocusOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }}>
        <DialogHeader><DialogTitle>Modifica cantiere</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <SiteAddressPicker value={f} onChange={(patch) => setF({ ...f, ...patch })} />
          <p className="text-[10px] text-amber-600">
            Se non ricalcoli l'indirizzo selezionandolo dai suggerimenti, distanza e comune/provincia/CAP restano quelli già salvati.
          </p>
          <div><Label>Nome cantiere *</Label>
            <div className="flex gap-2">
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
              <NomeDaIndirizzo scheda={f} onNome={(nome) => setF((p) => ({ ...p, name: nome }))} />
            </div>
          </div>
          <SceltaCommittente value={f.client_id} testo={f.client}
                               onChange={(id) => setF({ ...f, client_id: id })} />
          <div><Label>Tipo attività</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3"
                    value={f.activity_type} onChange={(e) => setF({ ...f, activity_type: e.target.value })}>
              {types.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data inizio prevista</Label>
              <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
            <div><Label>Data fine prevista</Label>
              <Input type="date" value={f.end_date_planned} onChange={(e) => setF({ ...f, end_date_planned: e.target.value })} /></div>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={f.skip_weekends}
                   onChange={(e) => setF({ ...f, skip_weekends: e.target.checked })} />
            <span>
              Escludi sabato e domenica dalla pianificazione
              <span className="block text-[10px] text-slate-500">
                Togli la spunta se sul cantiere si lavora anche nel fine settimana.
              </span>
            </span>
          </label>
          <p className="text-[10px] text-amber-600">
            Superata la data di fine lavori il cantiere passa automaticamente fra i terminati e
            resta consultabile nel calendario di pianificazione.
          </p>
          <div><Label>Stato cantiere</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3"
                    value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
              <option value="attivo">Attivo</option>
              <option value="sospeso">Sospeso</option>
              <option value="chiuso">Chiuso</option>
            </select>
            <p className="text-[10px] text-slate-500 mt-1">
              Usa "Sospeso" per fermi temporanei (es. maltempo, attese autorizzative) e "Chiuso" a lavori ultimati.
            </p>
          </div>
          <div><Label>Note</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>

          {/* Quello che serve al frontespizio del POS e non sta altrove. */}
          <div className="pt-2 border-t border-slate-200 space-y-3">
            <div className="text-xs uppercase tracking-wider text-slate-400">
              Per il POS di cantiere
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Numero di commessa</Label>
                <Input value={f.numero_commessa} placeholder="es. C-2026-118"
                       onChange={(e) => setF({ ...f, numero_commessa: e.target.value })}
                       data-testid="numero-commessa" /></div>
              <div><Label>POS n°</Label>
                <Input value={f.pos_numero} placeholder="es. 057-2026"
                       onChange={(e) => setF({ ...f, pos_numero: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <Label>Ruoli da riportare sul POS</Label>
              {["impresa", "committenza"].map((lato) => {
                const quali = ruoli.filter((r) => r.lato === lato);
                if (!quali.length) return null;
                return (
                  <div key={lato} className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                      {lato === "impresa" ? "Nostri" : "Della committenza"}
                    </div>
                    {quali.map((r) => (
                      <div key={r.codice} className="flex items-center gap-2">
                        <span className="text-xs text-slate-600 w-52 flex-shrink-0 truncate"
                              title={r.nome}>
                          {r.nome}
                        </span>
                        {r.automatico || r.dalla_squadra ? (
                          <div className="flex-1 h-8 px-3 flex items-center rounded border border-slate-200 bg-slate-50 text-sm text-slate-600">
                            {r.automatico || "dal preposto della squadra"}
                          </div>
                        ) : (
                          <Input className="h-8 text-sm" value={f.ruoli[r.codice] || ""}
                                 placeholder="nome e cognome"
                                 onChange={(e) => scriviRuolo(r.codice, e.target.value)} />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
              <p className="text-[10px] text-slate-500">
                Il datore di lavoro e il caposquadra li sa già l&apos;app: il primo è
                sempre lo stesso, il secondo è il preposto della squadra assegnata.
                Gli altri si stampano solo se compilati.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">Salva modifiche</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// L'avviso dei sopralluoghi esterni sulla scheda del cantiere.
//
// Quando un ispettore entra con il codice del preposto, il giro che fa -
// i QR che apre, cosa ci trova, le difformita' che scrive - finisce qui,
// sopra le schede del cantiere: una visita in corso e' rossa, l'ultima
// chiusa resta come promemoria con tutto quello che e' stato guardato.
function SopralluoghiEsterni({ visite, aperta, onApri, puoScaricare }) {
  const inCorso = visite.filter((v) => v.aperta);
  const chiuse = visite.filter((v) => !v.aperta);
  const quando = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  const colore = { rosso: "bg-red-100 text-red-700", giallo: "bg-amber-100 text-amber-700", verde: "bg-emerald-100 text-emerald-700" };
  const scarica = async (v) => {
    try {
      const { data } = await api.get(`/visite/${v.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `Sopralluogo ${v.nome}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Errore nella generazione del rapporto"); }
  };

  const Riga = ({ v, viva }) => {
    const espansa = aperta === v.id;
    const problemi = v.alert_rossi + v.difformita_gravi;
    const attenzioni = v.alert_gialli + (v.quante_difformita - v.difformita_gravi);
    return (
      <div className={`rounded-lg border ${viva ? "border-red-300 bg-red-50" : problemi ? "border-red-200 bg-white" : attenzioni ? "border-amber-200 bg-white" : "border-slate-200 bg-white"}`}>
        <button type="button" onClick={() => onApri(espansa ? null : v.id)}
                className="w-full text-left px-3 py-2 flex items-center gap-3">
          <ShieldQuestion className={`w-5 h-5 flex-shrink-0 ${viva ? "text-red-600" : "text-slate-500"}`} />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold truncate">
              {viva ? "Sopralluogo in corso: " : "Sopralluogo del " + quando(v.aperta_il).slice(0, 10) + ": "}
              {v.nome}{v.azienda ? ` (${v.azienda})` : ""}{v.ruolo ? ` · ${v.ruolo}` : ""}
            </span>
            <span className="block text-xs text-slate-500">
              {viva ? `dalle ${quando(v.aperta_il).slice(-5)}` : `${quando(v.aperta_il).slice(-5)}–${quando(v.chiusa_il).slice(-5)}, ${v.durata_minuti ?? "?"} min`}
              {" · "}{v.qr_distinti} QR aperti
              {v.alert_rossi > 0 && <span className="text-red-600 font-semibold"> · {v.alert_rossi} rossi</span>}
              {v.alert_gialli > 0 && <span className="text-amber-700 font-semibold"> · {v.alert_gialli} gialli</span>}
              {v.quante_difformita > 0 && <span className="text-red-600 font-semibold"> · {v.quante_difformita} difformità scritte</span>}
              {!viva && v.motivo_chiusura_nome ? ` · ${v.motivo_chiusura_nome.toLowerCase()}` : ""}
            </span>
          </span>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${espansa ? "rotate-180" : ""}`} />
        </button>
        {espansa && (
          <div className="px-3 pb-3 space-y-2 text-sm">
            {v.tappe.length === 0 && <p className="text-xs text-slate-500">Non ha ancora aperto nessun QR.</p>}
            {v.tappe.map((t, i) => (
              <div key={i} className="border-t border-slate-100 pt-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 tabular-nums">{quando(t.quando).slice(-5)}</span>
                  <span className="font-medium truncate">{t.tipo_nome}: {t.nome}</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${colore[t.esito] || "bg-slate-100 text-slate-500"}`}>
                    {t.esito === "verde" ? "in regola" : t.esito === "giallo" ? "attenzione" : t.esito === "rosso" ? "non in regola" : t.esito || "—"}
                  </span>
                </div>
                {t.rilievi.length > 0 && (
                  <ul className="text-xs text-slate-600 pl-4 list-disc mt-0.5">
                    {t.rilievi.map((r, j) => <li key={j}>{r.cosa}{r.dettaglio ? `: ${r.dettaglio}` : ""}</li>)}
                  </ul>
                )}
              </div>
            ))}
            {v.difformita.map((d, i) => (
              <div key={"d" + i} className="border-t border-red-100 pt-1.5">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">{d.gravita_nome || d.gravita}</span>
                  {d.riferimento_nome && <span className="text-xs text-slate-500">su {d.riferimento_nome}</span>}
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-line">{d.descrizione}</p>
              </div>
            ))}
            {puoScaricare && !viva && (
              <div className="pt-1">
                <Button variant="outline" size="sm" onClick={() => scarica(v)}>
                  <Download className="w-3 h-3 mr-1" /> Rapporto della visita
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2" data-testid="sopralluoghi-esterni">
      {inCorso.map((v) => <Riga key={v.id} v={v} viva />)}
      {chiuse.slice(0, inCorso.length ? 1 : 2).map((v) => <Riga key={v.id} v={v} viva={false} />)}
      {chiuse.length > 2 && (
        <p className="text-xs text-slate-400 px-1">Altri {chiuse.length - 2} sopralluoghi passati: in Sistemi integrati › Visite esterne.</p>
      )}
    </div>
  );
}

